import { API_BASE_URL, ROUTES } from './config';
import { errorFromResponse } from './errors';
import { tokenStore } from './tokenStore';
import { http } from './http';
import type { MediaPhase, JobMediaItem } from '../types/domain';

/**
 * WorkerService's MediaController — real job before/after photo storage (Prompt 6.2), not the
 * `/media/complete` two-phase design (which assumed a presigned-upload client that was never
 * built). No public URL for any job photo — reads always require the Bearer token, so both
 * upload and fetch bypass the JSON `http` client, same as `imagesApi.upload`.
 */
export const mediaApi = {
  async upload(file: File, jobId: string, phase: MediaPhase = 'Other', roomTag?: string): Promise<{ mediaId: string }> {
    const form = new FormData();
    form.append('file', file);
    form.append('jobId', jobId);
    form.append('phase', phase);
    if (roomTag) form.append('roomTag', roomTag);

    const token = tokenStore.get()?.accessToken;
    const response = await fetch(`${API_BASE_URL}${ROUTES.media}/upload`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body: form,
    });
    if (!response.ok) {
      const problem = await response.json().catch(() => null);
      throw errorFromResponse(response.status, problem, response.headers.get('X-Correlation-Id'));
    }
    return response.json();
  },

  listForJob(jobId: string): Promise<JobMediaItem[]> {
    return http.get(`${ROUTES.media}/job/${jobId}`);
  },

  /** Fetches the actual bytes with the Bearer token attached — a plain `<img src>` can't carry
   * it, since this route isn't cookie-authenticated. Caller turns the Blob into an object URL. */
  async fetchFile(mediaId: string): Promise<Blob> {
    const token = tokenStore.get()?.accessToken;
    const response = await fetch(`${API_BASE_URL}${ROUTES.media}/${mediaId}/file`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!response.ok) {
      const problem = await response.json().catch(() => null);
      throw errorFromResponse(response.status, problem, response.headers.get('X-Correlation-Id'));
    }
    return response.blob();
  },
};
